import { cacheService, EnvType } from "../services";
import { IApiClient } from "../api-client";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetAppInfoResponse } from "../Types/get-app-info-response";
import { GetAuthModesResponse } from "../Types/get-auth-modes-response";
import { GetAlertsResponse } from "../Types/get-alerts-response";
import { GetAuthModesRequest } from "../Types/get-auth-modes-request";
import { LoginRequest } from "../Types/login-request";
import { LoginResponse } from "../Types/login-response";
import { DbPlatformAlertInfos } from "../Types/db-platform-alert-infos";
import { GetSearchHistoryResponse } from "../Types/get-search-history-response";
import { UserAdvancedInfos } from "../Types/user-advanced-infos";
import { UpdatePasswordRequest } from "../Types/update-password-request";
import { GetUsersListAdvancedResponse } from "../Types/get-users-list-advanced-response";
import { GetUsersListBasicResponse } from "../Types/get-users-list-basic-response";
import { GetGroupedUsersListAdvancedResponse } from "../Types/get-grouped-users-list-advanced-response";
import { GetGroupedUsersListBasicResponse } from "../Types/get-grouped-users-list-basic-response";
import { GetLdapUsersResponse } from "../Types/get-ldap-users-response";
import { SyncLdapUsersRequest } from "../Types/sync-ldap-users-request";
import { UpdateNotesRequest } from "../Types/update-notes-request";
import { GetAvailableSearchQueriesResponse } from "../Types/get-available-search-queries-response";
import { DbSearchAdvancedQueryInfos } from "../Types/db-search-advanced-query-infos";
import { GetGroupEnumsResponse } from "../Types/get-group-enums-response";
import { ApproveWishesRequest } from "../Types/approve-wishes-request";
import { DbMailingListInfos } from "../Types/db-mailing-list-infos";
import { DeclinesWishlistRequest } from "../Types/declines-wishlist-request";
import { ForgotPasswordRequest } from "../Types/forgot-password-request";
import { GetAclsMappingResponse } from "../Types/get-acls-mapping-response";
import { GetEntitiesListResponse } from "../Types/get-entities-list-response";
import { GetGroupsListResponse } from "../Types/get-groups-list-response";
import { GetMailingListResponse } from "../Types/get-mailing-list-response";
import { GetWishResponse } from "../Types/get-wish-response";
import { GetWishlistResponse } from "../Types/get-wishlist-response";
import { PlatformResourceType } from "../Types/platform-resource-type";
import { CreateGroupRequest } from "../Types/create-group-request";
import { GetUserResponse } from "../Types/get-user-response";
import { GetGroupRolesListResponse } from "../Types/get-group-roles-list-response";
import { CreateGroupRoleRequest } from "../Types/create-group-role-request";
import { GetNotesResponses } from "../Types/get-notes-response";
import { GetSharedAccountsResponse } from "../Types/get-shared-accounts-response";
import { UpdateGroupsRequest } from "../Types/update-groups-request";
import { MfaValidateTokenRequest } from "../Types/mfa-validate-token-request";
import { GetCofResponse } from "../Types/get-cof-response";
import { GetRecentEmailsResponse, RecentEmailContext } from "../Types/get-recent-emails-response";
import { UserVerifyOrResetPasswordResponse } from "../Types/user-verify-or-reset-password-response";
import { VerifyOrResetPasswordUserRequest } from "../Types/user-verify-or-reset-password-request";
import { ChangePasswordRequest } from "../Types/change-password-request";

interface IUserApiClient {
  Login(login: LoginRequest): Promise<LoginResponse>;
  LoginByKey(key: string): Promise<LoginResponse>;
  LoginEmulate(userId: number): Promise<LoginResponse>;
  LoginByAccessKey(key: string): Promise<LoginResponse>;
  GetPlatformIcon(url: string): string;
  GetPlatformResourceImage(type: PlatformResourceType): string;
  GetPlatformResourceImageByAssetShareAuthKey(type: PlatformResourceType, shareKey: string): string;
  GetPlatformResourceImageByMediaShareAuthKey(type: PlatformResourceType, shareKey: string): string;
  Logout(): Promise<GenericResponseStd>;
  GetAppInfo(): Promise<GetAppInfoResponse>;
  GetAuthModes(request: GetAuthModesRequest): Promise<GetAuthModesResponse>;
  GetActiveAlertsList(): Promise<GetAlertsResponse>;
  GetAllAlertsList(): Promise<GetAlertsResponse>;
  CreateAlert(request: DbPlatformAlertInfos): Promise<GenericResponseStd>;
  UpdateAlert(request: DbPlatformAlertInfos): Promise<GenericResponseStd>;
  DeleteAlert(id: number): Promise<GenericResponseStd>;
  CreateUser(user: UserAdvancedInfos): Promise<GenericResponseStd>;
  UpdateUser(user: UserAdvancedInfos): Promise<GenericResponseStd>;
  UpdatePasswordBySession(user: UpdatePasswordRequest): Promise<GenericResponseStd>;
  DeleteUser(id: number): Promise<GenericResponseStd>;
  SearchUsers(searchPattern: string): Promise<GetUsersListAdvancedResponse>;
  SearchUsersBasic(searchPattern: string): Promise<GetUsersListBasicResponse>;
  GetUsersAdvancedList(): Promise<GetGroupedUsersListAdvancedResponse>;
  GetUsersBasicList(): Promise<GetGroupedUsersListBasicResponse>;
  GetAllUsersBasic(includeExternal?: boolean): Promise<GetUsersListBasicResponse>;
  GetLdapUsers(): Promise<GetLdapUsersResponse>;
  SyncLdapUsers(request: SyncLdapUsersRequest): Promise<GenericResponseStd>;
  DownloadUsersAsCsv(): Promise<GenericResponseStd>;
  GetMyNotes(): Promise<GetNotesResponses>;
  UpdateMyNotes(text: UpdateNotesRequest): Promise<GenericResponseStd>;
  GetAvailableSearchQueries(): Promise<GetAvailableSearchQueriesResponse>;
  PutSearchQueries(request: DbSearchAdvancedQueryInfos): Promise<GenericResponseStd>;
  DeleteSearchQuery(id: number): Promise<GenericResponseStd>;
  GetGroupsEnums(): Promise<GetGroupEnumsResponse>;
  GetGroupsList(): Promise<GetGroupsListResponse>;
  GetAclsMappingList(): Promise<GetAclsMappingResponse>;
  GetUsersListByGroupId(id: number): Promise<GetUsersListAdvancedResponse>;
  GetUsersListByProviderId(id: number): Promise<GetUsersListBasicResponse>;
  GetUsersListByCustomerId(id: number): Promise<GetUsersListAdvancedResponse>;
  CreateGroup(request: CreateGroupRequest): Promise<GenericResponseStd>;
  UpdateGroup(request: CreateGroupRequest): Promise<GenericResponseStd>;
  DeleteGroup(id: number): Promise<GenericResponseStd>;
  GetEntitiesList(): Promise<GetEntitiesListResponse>;
  GetMailingList(): Promise<GetMailingListResponse>;
  CreateMailingList(request: DbMailingListInfos): Promise<GenericResponseStd>;
  UpdateMailingList(request: DbMailingListInfos): Promise<GenericResponseStd>;
  DeleteMailingList(id: number): Promise<GenericResponseStd>;
  ForgotPasswordRequest(request: ForgotPasswordRequest): Promise<GenericResponseStd>;
  ForgotPasswordReset(request: VerifyOrResetPasswordUserRequest): Promise<GenericResponseStd>;
  ChangePassword(request: ChangePasswordRequest): Promise<GenericResponseStd>;
  UpdateMyPassword(request: UpdatePasswordRequest): Promise<GenericResponseStd>;
  GetWishlist(): Promise<GetWishlistResponse>;
  GetPagedWishlist(pageNb: number, pageSize: number): Promise<GetWishlistResponse>;
  GetWishById(id: number): Promise<GetWishResponse>;
  ApproveWishes(request: ApproveWishesRequest): Promise<GenericResponseStd>;
  DeclineWishes(request: DeclinesWishlistRequest): Promise<GenericResponseStd>;
  DeleteWish(id: number): Promise<GenericResponseStd>;
  GetSearchHistory(): Promise<GetSearchHistoryResponse>;
  GetGroupRolesList(): Promise<GetGroupRolesListResponse>;
  CreateGroupRole(request: CreateGroupRoleRequest): Promise<GenericResponseStd>;
  UpdateGroupRole(request: CreateGroupRoleRequest): Promise<GenericResponseStd>;
  DeleteGroupRole(id: number): Promise<GenericResponseStd>;
  VerifyUser(request: VerifyOrResetPasswordUserRequest): Promise<UserVerifyOrResetPasswordResponse>;
  ResendVerifyEmail(userId: number): Promise<UserVerifyOrResetPasswordResponse>;
  ValidateMfaToken(request: MfaValidateTokenRequest): Promise<LoginResponse>;
}

export default class UserApiClient implements IUserApiClient {
  readonly servicePort = 9501;
  readonly serviceBaseUrl = "user-service/api";

  userApiClient: IApiClient;

  constructor(userApiClient: IApiClient, envType: EnvType) {
    this.userApiClient = userApiClient;
    if (envType === "local") this.userApiClient.client.defaults.baseURL += `${this.servicePort}/`;
  }

  async ValidateMfaToken(request: MfaValidateTokenRequest): Promise<LoginResponse> {
    return await this.userApiClient.post<MfaValidateTokenRequest, LoginResponse>(
      `${this.serviceBaseUrl}/mfa/validate-token`,
      request
    );
  }

  async Login(login: LoginRequest): Promise<LoginResponse> {
    return await this.userApiClient.post<LoginRequest, LoginResponse>(`${this.serviceBaseUrl}/login`, login);
  }

  async LoginByKey(key: string): Promise<LoginResponse> {
    return await this.userApiClient.post<null, LoginResponse>(`${this.serviceBaseUrl}/login/${key}`, null);
  }

  async LoginEmulate(userId: number): Promise<LoginResponse> {
    return await this.userApiClient.post<null, LoginResponse>(`${this.serviceBaseUrl}/login-emulate/${userId}`, null);
  }

  async LoginByAccessKey(key: string): Promise<LoginResponse> {
    return await this.userApiClient.post<null, LoginResponse>(
      `${this.serviceBaseUrl}/login-external-access/${key}`,
      null
    );
  }
  async LoginSharedCrossAccount(userId: number): Promise<LoginResponse> {
    return await this.userApiClient.post<LoginRequest, LoginResponse>(`${this.serviceBaseUrl}/login-shared/${userId}`);
  }

  async GetSharedAccounts(): Promise<GetSharedAccountsResponse> {
    return await this.userApiClient.get<GetSharedAccountsResponse>(`${this.serviceBaseUrl}/shared-accounts`);
  }

  GetPlatformIcon(url: string): string {
    return this.userApiClient.client.defaults.baseURL + `${this.serviceBaseUrl}/platforms/icon/${url}`;
  }

  GetPlatformIconById(id: number): string {
    return this.userApiClient.client.defaults.baseURL + `${this.serviceBaseUrl}/platforms/icon/id/${id}`;
  }

  GetPlatformResourceImage(type: PlatformResourceType): string {
    return (
      this.userApiClient.client.defaults.baseURL +
      `${this.serviceBaseUrl}/platforms/resources/image-render/${type}/${cacheService.GetAuthKey()}`
    );
  }

  GetPlatformResourceImageByAssetShareAuthKey(type: PlatformResourceType, shareKey: string): string {
    return (
      this.userApiClient.client.defaults.baseURL +
      `${this.serviceBaseUrl}/platforms/resources/image-render/${type}/asset-share/${shareKey}`
    );
  }

  GetPlatformResourceImageByMediaShareAuthKey(type: PlatformResourceType, shareKey: string): string {
    return (
      this.userApiClient.client.defaults.baseURL +
      `${this.serviceBaseUrl}/platforms/resources/image-render/${type}/media-share/${shareKey}`
    );
  }

  async Logout(): Promise<GenericResponseStd> {
    return await this.userApiClient.post<null, GenericResponseStd>(`${this.serviceBaseUrl}/logout`, null);
  }

  async GetAppInfo(): Promise<GetAppInfoResponse> {
    return await this.userApiClient.get<GetAppInfoResponse>(`${this.serviceBaseUrl}/appinfo`);
  }

  async GetAuthModes(request: GetAuthModesRequest): Promise<GetAuthModesResponse> {
    return await this.userApiClient.post<GetAuthModesRequest, GetAuthModesResponse>(
      `${this.serviceBaseUrl}/auth-modes`,
      request
    );
  }

  async GetActiveAlertsList(): Promise<GetAlertsResponse> {
    return await this.userApiClient.get<GetAlertsResponse>(`${this.serviceBaseUrl}/alerts/active`);
  }

  async GetAllAlertsList(): Promise<GetAlertsResponse> {
    return await this.userApiClient.get<GetAlertsResponse>(`${this.serviceBaseUrl}/alerts`);
  }

  async CreateAlert(request: DbPlatformAlertInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.post<DbPlatformAlertInfos, GenericResponseStd>(
      `${this.serviceBaseUrl}/alerts`,
      request
    );
  }

  async UpdateAlert(request: DbPlatformAlertInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.put<DbPlatformAlertInfos, GenericResponseStd>(
      `${this.serviceBaseUrl}/alerts`,
      request
    );
  }

  async DeleteAlert(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.delete<null, GenericResponseStd>(`${this.serviceBaseUrl}/alerts/${id}`);
  }

  async CreateUser(user: UserAdvancedInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.post<UserAdvancedInfos, GenericResponseStd>(`${this.serviceBaseUrl}/users`, user);
  }

  async UpdateUser(user: UserAdvancedInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.put<UserAdvancedInfos, GenericResponseStd>(`${this.serviceBaseUrl}/users`, user);
  }

  async UpdatePasswordBySession(user: UpdatePasswordRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.put<UpdatePasswordRequest, GenericResponseStd>(
      `${this.serviceBaseUrl}/users/password`,
      user
    );
  }

  async DeleteUser(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.delete<null, GenericResponseStd>(`${this.serviceBaseUrl}/users/${id}`);
  }

  async SearchUsers(searchPattern: string): Promise<GetUsersListAdvancedResponse> {
    return await this.userApiClient.get<GetUsersListAdvancedResponse>(
      `${this.serviceBaseUrl}/users/search/${searchPattern}`
    );
  }

  async SearchUsersBasic(searchPattern: string): Promise<GetUsersListBasicResponse> {
    return await this.userApiClient.get<GetUsersListBasicResponse>(
      `${this.serviceBaseUrl}/users/search/${searchPattern}/basic`
    );
  }

  async GetUsersAdvancedList(): Promise<GetGroupedUsersListAdvancedResponse> {
    return await this.userApiClient.get<GetGroupedUsersListAdvancedResponse>(`${this.serviceBaseUrl}/users/grouped`);
  }

  async GetUsersBasicList(): Promise<GetGroupedUsersListBasicResponse> {
    return await this.userApiClient.get<GetGroupedUsersListBasicResponse>(`${this.serviceBaseUrl}/users/grouped/basic`);
  }

  async GetAllUsersBasic(includeExternal?: boolean): Promise<GetUsersListBasicResponse> {
    if (includeExternal)
      return await this.userApiClient.get<GetUsersListBasicResponse>(
        `${this.serviceBaseUrl}/users/basic/${includeExternal}`
      );
    return await this.userApiClient.get<GetUsersListBasicResponse>(`${this.serviceBaseUrl}/users/basic`);
  }

  async GetLdapUsers(): Promise<GetLdapUsersResponse> {
    return await this.userApiClient.get<GetLdapUsersResponse>(`${this.serviceBaseUrl}/users/ldap`);
  }

  async SyncLdapUsers(request: SyncLdapUsersRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.post<SyncLdapUsersRequest, GenericResponseStd>(
      `${this.serviceBaseUrl}/users/ldap`,
      request
    );
  }

  async DownloadUsersAsCsv(): Promise<GenericResponseStd> {
    return await this.userApiClient.get<GenericResponseStd>(`${this.serviceBaseUrl}/users/csv`);
  }

  async GetMyNotes(): Promise<GetNotesResponses> {
    return await this.userApiClient.get<GetNotesResponses>(`${this.serviceBaseUrl}/users/me/notes`);
  }

  async UpdateMyNotes(text: UpdateNotesRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.put<UpdateNotesRequest, GenericResponseStd>(
      `${this.serviceBaseUrl}/users/me/notes`,
      text
    );
  }

  async GetAvailableSearchQueries(): Promise<GetAvailableSearchQueriesResponse> {
    return await this.userApiClient.get<GetAvailableSearchQueriesResponse>(`${this.serviceBaseUrl}/search-query`);
  }

  async PutSearchQueries(request: DbSearchAdvancedQueryInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.put<DbSearchAdvancedQueryInfos, GenericResponseStd>(
      `${this.serviceBaseUrl}/search-query`,
      request
    );
  }

  async DeleteSearchQuery(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.delete<null, GenericResponseStd>(`${this.serviceBaseUrl}/search-query/${id}`);
  }

  async GetGroupsEnums(): Promise<GetGroupEnumsResponse> {
    return await this.userApiClient.get<GetGroupEnumsResponse>(`${this.serviceBaseUrl}/groups/enums`);
  }

  async GetGroupsList(): Promise<GetGroupsListResponse> {
    return await this.userApiClient.get<GetGroupsListResponse>(`${this.serviceBaseUrl}/groups`);
  }

  async GetAclsMappingList(): Promise<GetAclsMappingResponse> {
    return await this.userApiClient.get<GetAclsMappingResponse>(`${this.serviceBaseUrl}/groups/acls`);
  }

  async GetUsersListByGroupId(id: number): Promise<GetUsersListAdvancedResponse> {
    return await this.userApiClient.get<GetUsersListAdvancedResponse>(`${this.serviceBaseUrl}/groups/${id}`);
  }

  async GetUsersListByProviderId(id: number): Promise<GetUsersListBasicResponse> {
    return await this.userApiClient.get<GetUsersListBasicResponse>(`${this.serviceBaseUrl}/users/provider/${id}`);
  }

  async GetUsersListByCustomerId(id: number): Promise<GetUsersListAdvancedResponse> {
    return await this.userApiClient.get<GetUsersListAdvancedResponse>(`${this.serviceBaseUrl}/users/customer/${id}`);
  }

  async GetGroupRolesList(): Promise<GetGroupRolesListResponse> {
    return await this.userApiClient.get<GetGroupRolesListResponse>(`${this.serviceBaseUrl}/roles`);
  }

  async CreateGroupRole(request: CreateGroupRoleRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.post<CreateGroupRoleRequest, GenericResponseStd>(
      `${this.serviceBaseUrl}/roles`,
      request
    );
  }

  async UpdateGroupRole(request: CreateGroupRoleRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.put<CreateGroupRoleRequest, GenericResponseStd>(
      `${this.serviceBaseUrl}/roles`,
      request
    );
  }

  async DeleteGroupRole(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.delete<null, GenericResponseStd>(`${this.serviceBaseUrl}/roles/${id}`);
  }

  async EditProfilePicture(
    formData: FormData,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GetUserResponse> {
    return await this.userApiClient.postUploadFormData(
      `${this.serviceBaseUrl}/users/me/picture`,
      formData,
      abortSignal,
      onUploadProgressCallback
    );
  }

  async EditPlatformIcon(
    formData: FormData,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GetUserResponse> {
    return await this.userApiClient.postUploadFormData(
      `${this.serviceBaseUrl}/platforms/icon`,
      formData,
      abortSignal,
      onUploadProgressCallback
    );
  }

  async CreateGroup(request: CreateGroupRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.post<CreateGroupRequest, GenericResponseStd>(
      `${this.serviceBaseUrl}/groups`,
      request
    );
  }

  async UpdateGroup(request: CreateGroupRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.put<CreateGroupRequest, GenericResponseStd>(
      `${this.serviceBaseUrl}/groups`,
      request
    );
  }

  async UpdateGroups(request: UpdateGroupsRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.put<UpdateGroupsRequest, GenericResponseStd>(
      `${this.serviceBaseUrl}/groups/role`,
      request
    );
  }

  async DeleteGroup(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.delete<null, GenericResponseStd>(`${this.serviceBaseUrl}/groups/${id}`);
  }

  async GetEntitiesList(): Promise<GetEntitiesListResponse> {
    return await this.userApiClient.get<GetEntitiesListResponse>(`${this.serviceBaseUrl}/entities`);
  }

  async GetMailingList(): Promise<GetMailingListResponse> {
    return await this.userApiClient.get<GetMailingListResponse>(`${this.serviceBaseUrl}/mailinglists`);
  }

  async CreateMailingList(request: DbMailingListInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.post<DbMailingListInfos, GenericResponseStd>(
      `${this.serviceBaseUrl}/mailinglists`,
      request
    );
  }

  async UpdateMailingList(request: DbMailingListInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.put<DbMailingListInfos, GenericResponseStd>(
      `${this.serviceBaseUrl}/mailinglists`,
      request
    );
  }

  async DeleteMailingList(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.delete<null, GenericResponseStd>(`${this.serviceBaseUrl}/mailinglists/${id}`);
  }

  async ForgotPasswordRequest(request: ForgotPasswordRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.post<ForgotPasswordRequest, GenericResponseStd>(
      `${this.serviceBaseUrl}/password/forgot-request`,
      request
    );
  }

  async ForgotPasswordReset(request: VerifyOrResetPasswordUserRequest): Promise<UserVerifyOrResetPasswordResponse> {
    return await this.userApiClient.post<VerifyOrResetPasswordUserRequest, UserVerifyOrResetPasswordResponse>(
      `${this.serviceBaseUrl}/password/forgot-reset`,
      request
    );
  }

  async ChangePassword(request: ChangePasswordRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.put<ChangePasswordRequest, GenericResponseStd>(
      `${this.serviceBaseUrl}/password/change`,
      request
    );
  }

  async UpdateMyPassword(request: UpdatePasswordRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.put<UpdatePasswordRequest, GenericResponseStd>(
      `${this.serviceBaseUrl}/users/password`,
      request
    );
  }

  async GetWishlist(): Promise<GetWishlistResponse> {
    return await this.userApiClient.get<GetWishlistResponse>(`${this.serviceBaseUrl}/wishlist`);
  }

  async GetPagedWishlist(pageNb: number, pageSize: number): Promise<GetWishlistResponse> {
    return await this.userApiClient.get<GetWishlistResponse>(`${this.serviceBaseUrl}/wishlist/${pageNb}/${pageSize}`);
  }

  async GetWishById(id: number): Promise<GetWishResponse> {
    return await this.userApiClient.get<GetWishResponse>(`${this.serviceBaseUrl}/wishlist/${id}`);
  }

  async ApproveWishes(request: ApproveWishesRequest): Promise<GetWishResponse> {
    return await this.userApiClient.put<ApproveWishesRequest, GetWishResponse>(
      `${this.serviceBaseUrl}/wishlist/approve`,
      request
    );
  }

  async DeclineWishes(request: DeclinesWishlistRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.put<DeclinesWishlistRequest, GenericResponseStd>(
      `${this.serviceBaseUrl}/wishlist/decline`,
      request
    );
  }

  async DeleteWish(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.delete<null, GenericResponseStd>(`${this.serviceBaseUrl}/wishlist/${id}`);
  }

  async GetSearchHistory(): Promise<GetSearchHistoryResponse> {
    return await this.userApiClient.get<GetSearchHistoryResponse>(`${this.serviceBaseUrl}/users/search-history`);
  }

  async VerifyUser(request: VerifyOrResetPasswordUserRequest): Promise<UserVerifyOrResetPasswordResponse> {
    return await this.userApiClient.post<VerifyOrResetPasswordUserRequest, UserVerifyOrResetPasswordResponse>(
      `${this.serviceBaseUrl}/users/verify`,
      request
    );
  }

  async ResendVerifyEmail(userId: number): Promise<UserVerifyOrResetPasswordResponse> {
    return await this.userApiClient.post(`${this.serviceBaseUrl}/users/verify-resend/${userId}`);
  }

  async GetCof(): Promise<GetCofResponse> {
    return await this.userApiClient.get<GetCofResponse>(`${this.serviceBaseUrl}/cof`);
  }

  async GetCofNoAuth(platformId: number): Promise<GetCofResponse> {
    return await this.userApiClient.get<GetCofResponse>(`${this.serviceBaseUrl}/cof/${platformId}`);
  }

  async PutCof(): Promise<GenericResponseStd> {
    return await this.userApiClient.put<{}, GenericResponseStd>(`${this.serviceBaseUrl}/cof`);
  }

  async GetRecentEmails(context: RecentEmailContext): Promise<GetRecentEmailsResponse> {
    return await this.userApiClient.get<GetRecentEmailsResponse>(
      `${this.serviceBaseUrl}/users/me/recent-emails/${context}`
    );
  }

  //------Utils------
  GetApiUrl(): string {
    return this.userApiClient.client.defaults.baseURL + this.serviceBaseUrl;
  }
}
